import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  getApiErrorReason,
  useCreateCommitMutation,
  useSwitchBranch,
  type Branch
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog'
import { extractMutationError } from './commit-dialog-utils'

export interface BranchCommitDialogProps {
  branch: Branch
  open: boolean
  onOpenChange: (open: boolean) => void
}

type CommitNotice = { kind: 'info' | 'error'; text: string } | { kind: 'not-open'; text: string }

/**
 * Records the branch's working database as a new commit. The async pipeline
 * snapshots and advances the head on the request, then computes the itemized
 * delta in the background — so the call returns quickly and the new commit
 * appears immediately, its change set "computing" in history. Outcome handling:
 * success toasts the short hash (history refreshes via tag invalidation); a
 * moved head prompts a retry against the refetched branch; an unopened branch
 * offers the open recovery inline. Every commit is recorded — an unchanged
 * working DB becomes a labeled "no changes" save point, never an error.
 */
export function BranchCommitDialog({ branch, open, onOpenChange }: BranchCommitDialogProps) {
  const [createCommit, commitState] = useCreateCommitMutation()
  const { switchBranch, isOpening } = useSwitchBranch()
  const [title, setTitle] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [notice, setNotice] = React.useState<CommitNotice | null>(null)

  React.useEffect(() => {
    if (!open) {
      setTitle('')
      setDescription('')
      setNotice(null)
      commitState.reset()
    }
  }, [open, commitState])

  const submitting = commitState.isLoading
  const canSubmit = title.trim().length > 0 && !submitting

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canSubmit) return
    setNotice(null)
    // The git message convention: first line is the title, a blank line, then
    // the body — lists render the first line, detail renders both.
    const message = [title.trim(), description.trim()].filter(Boolean).join('\n\n')
    try {
      const commit = await createCommit({ branchId: branch.id, message }).unwrap()
      toast.success(`Committed ${commit.hash.slice(0, 8)} on ${branch.name}`)
      onOpenChange(false)
    } catch (err) {
      switch (getApiErrorReason(err)) {
        case 'HEAD_MOVED':
          // The failed mutation already invalidated the branch cache; the
          // refetched head is what the retry will commit against.
          setNotice({
            kind: 'error',
            text: 'The branch head moved while committing — try again.'
          })
          break
        case 'BRANCH_NOT_OPEN':
          setNotice({
            kind: 'not-open',
            text: 'This branch is not open — open it first, then commit.'
          })
          break
        case 'BRANCH_PROTECTED':
          setNotice({ kind: 'error', text: 'Protected branches advance only via merge.' })
          break
        default:
          setNotice({ kind: 'error', text: extractMutationError(err, 'Could not commit.') })
      }
    }
  }

  const handleOpenBranch = async () => {
    setNotice(null)
    try {
      await switchBranch(branch.id)
      setNotice({ kind: 'info', text: 'Branch opened — commit when ready.' })
    } catch {
      setNotice({ kind: 'error', text: `Could not open ${branch.name}.` })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return
        onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Commit changes on {branch.name}</DialogTitle>
          <DialogDescription>
            Records the branch&apos;s current state as a new commit and advances its head.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <Input
            value={title}
            onChange={(event) => {
              setTitle(event.target.value)
              setNotice(null)
            }}
            placeholder="Title"
            aria-label="Commit title"
            autoComplete="off"
            autoFocus
            disabled={submitting}
          />
          <Textarea
            value={description}
            onChange={(event) => {
              setDescription(event.target.value)
              setNotice(null)
            }}
            placeholder="Description (optional)"
            aria-label="Commit description"
            rows={3}
            disabled={submitting}
          />

          {submitting ? (
            <p className="flex items-center gap-2 text-xs text-ink-3">
              <Loader2 className="size-3 animate-spin" />
              Committing…
            </p>
          ) : null}

          {notice ? (
            <div
              role={notice.kind === 'info' ? 'status' : 'alert'}
              className={
                notice.kind === 'info'
                  ? 'rounded-md bg-st-idle-bg/40 px-3 py-2 text-xs text-ink-3'
                  : 'rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down'
              }
            >
              {notice.text}
              {notice.kind === 'not-open' ? (
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  className="ml-2"
                  onClick={() => void handleOpenBranch()}
                  disabled={isOpening}
                >
                  {isOpening ? <Loader2 className="animate-spin" /> : null}
                  Open branch
                </Button>
              ) : null}
            </div>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit || isOpening}>
              {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
              Commit
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
